import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight, ShieldCheck, Clock, MapPin, BadgeCheck } from "lucide-react";
import { Seo, faqJsonLd } from "../../components/Seo";
import { ServiceIcon } from "../../components/ServiceIcon";
import { Faq } from "../../components/Faq";
import { CallCta } from "../../components/CallCta";
import { Testimonials } from "../../components/Testimonials";
import { BUSINESS_FR, SERVICES_FR, QC_CITIES, HOME_FAQS_FR } from "../../data/site-fr";

const HERO_IMG = "https://images.unsplash.com/photo-1485230405346-71acb9518d9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMHBhdHJvbCUyMG5pZ2h0JTIwYnVpbGRpbmclMjBzdXJ2ZWlsbGFuY2V8ZW58MHx8fHwxNzkyMzc0Mjc2fDA&ixlib=rb-4.1.0&q=85";

const WHY_FR = [
  { icon: BadgeCheck, title: "Détenue et exploitée par des Autochtones", text: "Une entreprise de sécurité fièrement autochtone qui crée de l'emploi local et offre une protection respectueuse de la culture." },
  { icon: Clock, title: "Répartition et réponse 24/7", text: "Notre ligne ne ferme jamais. Gardiens, patrouilles et réponse aux alarmes, de jour comme de nuit, 365 jours par année." },
  { icon: MapPin, title: "Couverture Québec et Manitoba", text: "De Montréal à Winnipeg, de Gatineau à Thompson — un seul partenaire de sécurité pour toutes vos installations." },
  { icon: ShieldCheck, title: "Gardiens licenciés et formés", text: "Agents licenciés (BSP au Québec, licence provinciale au Manitoba) avec formation propre au site, supervision et rapports numériques." },
];

export default function HomeFr() {
  return (
    <div data-testid="home-fr-page">
      <Seo
        lang="fr-CA"
        title="Agence de Sécurité Québec | 1st Nations Security"
        description="Entreprise de sécurité détenue par des Autochtones — gardiens de sécurité, patrouilles mobiles, sécurité événementielle, ronde incendie. Québec et Manitoba, 24/7."
        path="/francais/"
        jsonLd={[faqJsonLd(HOME_FAQS_FR)]}
      />

      <div className="bg-white" data-testid="ccib-member-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
          <img
            src="/ccib-member.png"
            alt="Membre fier du Conseil canadien pour l'entreprise autochtone — Entreprise autochtone certifiée CCIB"
            className="h-16 sm:h-20 lg:h-24 w-auto object-contain"
          />
        </div>
      </div>

      <section className="relative overflow-hidden" data-testid="hero-fr-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Agent de patrouille de sécurité en service la nuit" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#0D0E11]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5 mb-6" data-testid="hero-fr-badge">
              <span className="w-2 h-2 rounded-full bg-[#FF9100]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100]">100 % détenue et exploitée par des Autochtones</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6">
              Services de Gardiennage au <span className="text-[#FF9100]">Québec</span> et au Manitoba
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl">
              1st Nations Security protège les entreprises, chantiers de construction, événements et communautés à Montréal, Laval, Longueuil, Lévis, Gatineau, Québec et partout au Manitoba — avec des gardiens licenciés, des patrouilles mobiles et une réponse rapide 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BUSINESS_FR.phoneHref} data-testid="hero-fr-call-btn" className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#FF9100] hover:bg-[#FF5500] text-[#0D0E11] font-bold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 active:scale-95">
                <Phone className="w-5 h-5" /> Appelez le {BUSINESS_FR.phone}
              </a>
              <a href={BUSINESS_FR.emailHref} data-testid="hero-fr-email-btn" className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#FF9100] hover:text-[#FF9100] text-white font-semibold px-8 py-4 rounded-full transition-all duration-200">
                <Mail className="w-5 h-5" /> Écrivez-nous
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="services-fr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] mb-3">Ce que nous faisons</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">Nos Services de Sécurité</h2>
          <p className="text-gray-400 max-w-3xl mb-12 leading-relaxed">
            Des gardiens de sécurité en uniforme aux patrouilles mobiles, en passant par la ronde incendie et la sécurité événementielle, chaque service est assuré par des professionnels formés et licenciés, appuyés par notre répartition 24/7.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES_FR.map((s) => (
              <Link
                key={s.slug}
                to={`/francais/services-de-securite/${s.slug}`}
                data-testid={`home-fr-service-card-${s.slug}`}
                className="group bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100] mb-4 group-hover:scale-110 transition-transform duration-300">
                  <ServiceIcon name={s.icon} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-[#FF9100] transition-colors">{s.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-4">{s.shortDesc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-[#FF9100]">
                  En savoir plus <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A0B0E]" data-testid="why-fr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] mb-3">Pourquoi nous</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-10">
            Une Compagnie de Sécurité Autochtone de Confiance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {WHY_FR.map((w, i) => (
              <div key={i} className="flex gap-4" data-testid={`why-fr-item-${i}`}>
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100]">
                  <w.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{w.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{w.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="villes-fr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] mb-3">Où nous travaillons</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">Villes du Québec Desservies</h2>
          <p className="text-gray-400 max-w-3xl mb-10 leading-relaxed">
            Nous offrons des services de gardiennage à Montréal, Laval, Longueuil, Lévis, Gatineau, Québec, Trois-Rivières, Sherbrooke, Saguenay et Drummondville — ainsi que dans 25 villes du Manitoba.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {QC_CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/francais/villes/${c.slug}`}
                data-testid={`home-fr-ville-card-${c.slug}`}
                className="group bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-xl px-4 py-4 transition-all duration-300 hover:-translate-y-0.5"
              >
                <p className="font-semibold text-white text-sm group-hover:text-[#FF9100] transition-colors">{c.name}</p>
                <p className="text-xs text-gray-500 mt-1">{c.region}</p>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-6">
            <Link to="/francais/villes" data-testid="home-fr-villes-all-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">
              Toutes les villes du Québec <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/service-areas" data-testid="home-fr-mb-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">
              Zones desservies au Manitoba (EN) <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A0B0E]" data-testid="faq-fr-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-8 text-center">
            Questions Fréquentes — Services de Sécurité
          </h2>
          <Faq items={HOME_FAQS_FR} testidPrefix="home-fr-faq" />
        </div>
      </section>

      <Testimonials fr />
      <CallCta heading="Obtenez une Couverture de Sécurité Aujourd'hui" sub="Parlez directement à notre équipe de répartition 24/7 — sans formulaire, sans attente." testid="cta-fr" />
    </div>
  );
}
